import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import {
  ConfirmDialog,
  ModuleHeader
} from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import {
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  MinimizedModalStrip,
} from 'components/ui';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { usePropertyTerms, useDeletePropertyTerms } from 'hooks/api/usePropertyTerms';
import { propertyService } from 'api/services/propertyService';
import { configService } from 'api/services/configService';
import type { PropertyTerms, PropertyTermsSearchRequest, Property } from 'api/types';
import type { SelectOption } from 'components/ui/SyutSelect';
import PropertyTermsModal from 'components/modals/modules/property-module/PropertyTermsModal';
import PropertyTermsViewModal from 'components/modals/modules/property-module/PropertyTermsViewModal';
import TermTypesModal from './TermTypesModal';

// ============================================================================
// TYPES
// ============================================================================

type ViewMode = 'list' | 'grid';

interface ColumnDefinition {
  id: string;
  label: string;
  field: string;
  required: boolean;
  defaultVisible: boolean;
  width?: number;
  sortable?: boolean;
}

// ============================================================================
// DROPDOWN OPTIONS
// ============================================================================

/** Formats a config key like BOOKING_CONFIRMATION → Booking Confirmation */
const formatTermTypeLabel = (value: string): string =>
  value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).replace(/\B\w+/g, w => w.toLowerCase());

const statusOptions: SelectOption[] = [
  { value: '', label: 'All Status' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

// ============================================================================
// COLUMN DEFINITIONS
// ============================================================================

const columnDefinitions: ColumnDefinition[] = [
  { id: 'select', label: '', field: 'select', required: true, defaultVisible: true, width: 50, sortable: false },
  { id: 'propertyName', label: 'Property', field: 'propertyId', required: false, defaultVisible: true, sortable: false },
  { id: 'termType', label: 'Term Type', field: 'termType', required: false, defaultVisible: true, sortable: true },
  { id: 'languageCode', label: 'Language', field: 'languageCode', required: false, defaultVisible: true, sortable: true },
  { id: 'termsText', label: 'Terms Text', field: 'termsText', required: false, defaultVisible: true, sortable: false },
  { id: 'validFrom', label: 'Valid From', field: 'validFrom', required: false, defaultVisible: true, sortable: true },
  { id: 'validTo', label: 'Valid To', field: 'validTo', required: false, defaultVisible: true, sortable: true },
  { id: 'isActive', label: 'Status', field: 'isActive', required: false, defaultVisible: true, sortable: true },
  { id: 'actions', label: 'Actions', field: 'actions', required: true, defaultVisible: true, width: 80, sortable: false },
];

// ============================================================================
// COLUMN SELECTOR COMPONENT
// ============================================================================

interface ColumnSelectorProps {
  columns: ColumnDefinition[];
  visibleColumns: string[];
  onApply: (visibleColumns: string[]) => void;
  onReset: () => void;
}

const ColumnSelector: React.FC<ColumnSelectorProps> = ({
  columns,
  visibleColumns: initialVisible,
  onApply,
  onReset,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<string[]>(initialVisible);
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSelectedColumns(initialVisible);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, initialVisible]);

  const toggleColumn = (columnId: string) => {
    const column = columns.find(col => col.id === columnId);
    if (column?.required) return;
    setSelectedColumns(prev =>
      prev.includes(columnId) ? prev.filter(id => id !== columnId) : [...prev, columnId]
    );
  };

  const handleSelectAll = () => {
    const allColumnIds = columns.filter(col => !col.required).map(col => col.id);
    const requiredIds = columns.filter(col => col.required).map(col => col.id);
    if (selectedColumns.length === columns.length) {
      setSelectedColumns(requiredIds);
    } else {
      setSelectedColumns([...requiredIds, ...allColumnIds]);
    }
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }} ref={panelRef}>
      <button
        className={`btn-icon ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title="Manage Columns"
        style={isOpen ? { background: 'var(--accent-gradient)', color: 'white', borderColor: 'var(--primary)' } : {}}
      >
        <IconSystem.grid size={18} />
      </button>

      {isOpen && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 0.5rem)',
          right: 0,
          width: '320px',
          maxHeight: '500px',
          background: 'var(--bg-primary)',
          border: '2px solid var(--primary)',
          borderRadius: '0.75rem',
          boxShadow: '0 12px 32px rgba(0, 0, 0, 0.15)',
          zIndex: 1000,
          animation: 'slideDown 0.2s ease',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
          <div style={{
            padding: '1rem 1.25rem',
            borderBottom: '2px solid var(--border-color)',
            background: 'var(--bg-secondary)',
            borderRadius: '0.75rem 0.75rem 0 0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
          }}>
            <h3 style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
              Manage Columns
            </h3>
            <button
              onClick={() => setIsOpen(false)}
              style={{ width: '1.5rem', height: '1.5rem', border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '0.25rem' }}
            >
              <IconSystem.x size={16} />
            </button>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '0.75rem 0', minHeight: 0 }}>
            {columns.map(column => (
              <div
                key={column.id}
                onClick={() => toggleColumn(column.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.625rem 1.25rem',
                  cursor: column.required ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  gap: '0.75rem',
                  opacity: column.required ? 0.6 : 1,
                }}
              >
                <div style={{
                  width: '1.125rem',
                  height: '1.125rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid var(--border-color)',
                  borderRadius: '0.25rem',
                  transition: 'all 0.2s ease',
                  flexShrink: 0,
                  ...(selectedColumns.includes(column.id) ? { background: 'var(--accent-gradient)', borderColor: 'var(--primary)', color: 'white' } : {}),
                }}>
                  {selectedColumns.includes(column.id) && <IconSystem.check size={14} />}
                </div>
                <span style={{ flex: 1, fontSize: '0.85rem', color: 'var(--text-primary)', userSelect: 'none' }}>
                  {column.label}{column.required && ' *'}
                </span>
              </div>
            ))}
          </div>

          <div style={{
            padding: '1rem',
            borderTop: '2px solid var(--border-color)',
            background: 'var(--bg-primary)',
            boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.05)',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
            flexShrink: 0,
          }}>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={handleSelectAll}
                style={{ flex: 1, padding: '0.5rem 0.75rem', fontSize: '0.75rem', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '0.5rem', cursor: 'pointer' }}
              >
                {selectedColumns.length === columns.length ? 'Deselect All' : 'Select All'}
              </button>
              <button
                onClick={() => setConfirmResetOpen(true)}
                style={{ flex: 1, padding: '0.5rem 0.75rem', fontSize: '0.75rem', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '0.5rem', cursor: 'pointer' }}
              >
                Reset Default
              </button>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => { setSelectedColumns(initialVisible); setIsOpen(false); }}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={() => { onApply(selectedColumns); setIsOpen(false); }}>
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmResetOpen}
        title="Reset Columns"
        message="Reset to default column configuration?"
        confirmLabel="Reset"
        cancelLabel="Cancel"
        onConfirm={() => { onReset(); setConfirmResetOpen(false); setIsOpen(false); }}
        onCancel={() => setConfirmResetOpen(false)}
      />
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

interface PropertyTermsMasterScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  /**
   * Subset of {'add','edit','delete','view','export'} the current user may
   * perform on this module — driven by admin.tbl_role_permissions
   * (passed through main_layout from MODULES_CONFIG.actions). Defaults to
   * [] (fail-closed): if the prop is missing, no action buttons render.
   */
  allowedActions?: string[];
}

const PropertyTermsMasterScreen: React.FC<PropertyTermsMasterScreenProps> = ({ moduleIcon, tabId, moduleName, allowedActions = [] }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();

  // --- Dynamic term type options from Config framework ---
  const [termTypeOptions, setTermTypeOptions] = useState<SelectOption[]>([]);

  // --- Property lookup for display names ---
  const [propertyLookup, setPropertyLookup] = useState<Record<string, Property>>({});

  // --- Server-side pagination & search state ---
  const [queryParams, setQueryParams] = useState<PropertyTermsSearchRequest>({
    page: 1,
    pageSize: 25,
  });
  const { data, pagination, refetch } = usePropertyTerms(queryParams);
  const { deletePropertyTerms, loading: isDeleting } = useDeletePropertyTerms();

  // --- View state ---
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() =>
    columnDefinitions.filter(col => col.defaultVisible).map(col => col.id)
  );

  // --- Filter state ---
  const [filterProperty, setFilterProperty] = useState('');
  const [filterTermType, setFilterTermType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // --- Modal state ---
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [termTypesOpen, setTermTypesOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'copy'>('create');
  const [selectedRecord, setSelectedRecord] = useState<PropertyTerms | null>(null);
  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState<string>('0');
  const copyInstanceCounter = useRef(0);
  const [copyInstanceId, setCopyInstanceId] = useState<string>('0');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  // --- Load property lookup + term type options from config ---
  const loadLookups = useCallback(async () => {
    try {
      const propRes = await propertyService.getAll({ pageSize: 500 });
      const lookup: Record<string, Property> = {};
      propRes.data.forEach(p => { lookup[p.id] = p; });
      setPropertyLookup(lookup);
    } catch { /* ignore */ }
    try {
      const cfgRes = await configService.getEffectiveConfig('PROPERTY_TERMS');
      const termTypesDef = (cfgRes.data || []).find(d => d.configKey === 'term_types');
      if (termTypesDef?.listValues?.length) {
        setTermTypeOptions(termTypesDef.listValues
          .filter(i => i.isActive)
          .map(i => ({
            value: i.value,
            label: formatTermTypeLabel(i.value),
          })));
      }
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadLookups(); }, [loadLookups]);

  // --- Async property options for filter panel ---
  const loadPropertyFilterOptions = useCallback(async (inputValue: string): Promise<SelectOption[]> => {
    const res = await propertyService.getAll({ searchTerm: inputValue || undefined, pageSize: 50, includeInactive: false });
    return (res.data || []).map(p => ({ value: p.id, label: `${p.propertyCode} - ${p.propertyName}` }));
  }, []);

  // --- Search debounce (server-side, 400ms) ---
  useEffect(() => {
    const timer = setTimeout(() => {
      setQueryParams(prev => ({
        ...prev,
        searchTerm: searchQuery || undefined,
        page: 1,
      }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // --- Server-side pagination handlers ---
  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams(prev => ({ ...prev, page: pageIndex + 1 }));
  }, []);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setQueryParams(prev => ({ ...prev, pageSize, page: 1 }));
  }, []);

  // --- Edit handler ---
  const handleEdit = useCallback((rec: PropertyTerms) => {
    const modalId = tabId ? `${tabId}-form-modal-edit-${rec.id}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId);
      modal?.onRestore?.();
      return;
    }
    setSelectedRecord(rec);
    setFormMode('edit');
    setFormModalOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  // --- View handler ---
  const handleView = useCallback((rec: PropertyTerms) => {
    const viewId = tabId ? `${tabId}-view-modal-${rec.id}` : null;
    if (viewId && isMinimized(viewId)) {
      const modal = getMinimizedModal(viewId);
      dismissMinimized(viewId);
      modal?.onRestore?.();
      return;
    }
    setSelectedRecord(rec);
    setViewModalOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  // --- Restore view handler ---
  const handleRestoreView = useCallback((recordId: string) => {
    loadLookups();
    const rec = data.find(r => r.id === recordId);
    if (rec) {
      setSelectedRecord(rec);
      setViewModalOpen(true);
    }
  }, [data, loadLookups]);

  // --- Copy handler (multi-instance) ---
  const handleCopy = useCallback((rec: PropertyTerms) => {
    if (formModalOpen) {
      formModalMinimizeRef.current?.();
    }
    const newId = String(++copyInstanceCounter.current);
    setCopyInstanceId(newId);
    setSelectedRecord(rec);
    setFormMode('copy');
    setFormModalOpen(true);
  }, [formModalOpen]);

  // --- Add New handler ---
  const handleAddNew = useCallback(() => {
    const newId = String(++createInstanceCounter.current);
    setCreateInstanceId(newId);
    setFormMode('create');
    setSelectedRecord(null);
    setFormModalOpen(true);
  }, []);

  // --- Delete handler ---
  const handleDeleteClick = useCallback((rec: PropertyTerms) => {
    setSelectedRecord(rec);
    setDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedRecord) return;
    try {
      await deletePropertyTerms(selectedRecord.id as string, selectedRecord.rowVersion);
      setDeleteModalOpen(false);
      notificationService.toast.success(`Property terms deleted successfully.`);
      setSelectedRecord(null);
      refetch();
    } catch {
      notificationService.toast.error('An error occurred while deleting. Please try again.');
    }
  }, [selectedRecord, deletePropertyTerms, refetch]);

  const handleSaveSuccess = useCallback(() => {
    setFormModalOpen(false);
    setSelectedRecord(null);
    refetch();
    loadLookups();
  }, [refetch, loadLookups]);

  const handleRestoreModal = useCallback((restoredMode: 'create' | 'edit' | 'copy', instanceId?: string, recordId?: string) => {
    loadLookups();
    if (instanceId) {
      if (restoredMode === 'copy') setCopyInstanceId(instanceId);
      else setCreateInstanceId(instanceId);
    }
    if (recordId) {
      const rec = data.find(r => r.id === recordId);
      if (rec) setSelectedRecord(rec);
    }
    setFormMode(restoredMode);
    setFormModalOpen(true);
  }, [data, loadLookups]);

  // --- Column definitions for SyutGrid ---
  const tableColumns: SyutGridColumn<PropertyTerms>[] = useMemo(() => [
    {
      id: 'propertyName',
      header: 'Property',
      accessorKey: 'propertyId',
      size: 200,
      enableSorting: false,
      cell: ({ row }) => {
        const rec = row.original;
        return (
          <span style={{ fontSize: '0.85rem' }}>
            {rec.propertyCode && rec.propertyName ? `${rec.propertyCode} - ${rec.propertyName}` : rec.propertyId}
          </span>
        );
      },
    },
    {
      id: 'termType',
      header: 'Term Type',
      accessorKey: 'termType',
      size: 180,
      enableSorting: true,
      cell: ({ getValue }) => {
        const val = getValue() as string;
        const label = termTypeOptions.find(o => o.value === val)?.label || val;
        return <span style={{ fontSize: '0.85rem' }}>{label}</span>;
      },
    },
    {
      id: 'languageCode',
      header: 'Language',
      accessorKey: 'languageCode',
      size: 100,
      enableSorting: true,
      cell: ({ getValue }) => {
        const val = getValue() as string;
        return <span style={{ fontSize: '0.85rem' }}>{val?.toUpperCase() || '-'}</span>;
      },
    },
    {
      id: 'termsText',
      header: 'Terms Text',
      accessorKey: 'termsText',
      size: 300,
      enableSorting: false,
      cell: ({ getValue }) => {
        const val = getValue() as string;
        if (!val) return <span style={{ fontSize: '0.85rem' }}>-</span>;
        const lines = val.split('\n').filter(l => l.trim());
        const firstLine = lines[0]?.length > 60 ? `${lines[0].substring(0, 60)}...` : lines[0] || '';
        return (
          <div style={{ fontSize: '0.85rem', lineHeight: '1.3' }}>
            <span>{firstLine}</span>
            {lines.length > 1 && (
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginLeft: '0.375rem' }}>
                (+{lines.length - 1} more line{lines.length - 1 > 1 ? 's' : ''})
              </span>
            )}
          </div>
        );
      },
    },
    {
      id: 'validFrom',
      header: 'Valid From',
      accessorKey: 'validFrom',
      size: 130,
      enableSorting: true,
      cell: ({ getValue }) => {
        const val = getValue() as string | undefined;
        return <span style={{ fontSize: '0.85rem' }}>{val ? new Date(val).toLocaleDateString() : '-'}</span>;
      },
    },
    {
      id: 'validTo',
      header: 'Valid To',
      accessorKey: 'validTo',
      size: 130,
      enableSorting: true,
      cell: ({ getValue }) => {
        const val = getValue() as string | undefined;
        return <span style={{ fontSize: '0.85rem' }}>{val ? new Date(val).toLocaleDateString() : '-'}</span>;
      },
    },
    {
      id: 'isActive',
      header: 'Status',
      accessorKey: 'isActive',
      size: 100,
      cell: ({ getValue }) => {
        const isActive = getValue() as boolean;
        return (
          <span className={`badge badge-${isActive ? 'active' : 'inactive'}`}>
            {isActive ? 'Active' : 'Inactive'}
          </span>
        );
      },
    },
  ], [termTypeOptions]);

  // --- Row actions ---
  const getRowActions = useCallback((rec: PropertyTerms): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [];
    if (allowedActions.includes('view')) {
      items.push({ id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => handleView(rec) });
    }
    if (allowedActions.includes('edit')) {
      items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => handleEdit(rec) });
    }
    if (allowedActions.includes('add')) {
      items.push({ id: 'copy', label: 'Copy', icon: <IconSystem.copy size={18} />, onClick: () => handleCopy(rec) });
    }
    if (allowedActions.includes('delete')) {
      items.push({ id: 'delete', label: 'Delete', icon: <IconSystem.trash size={18} />, onClick: () => handleDeleteClick(rec), variant: 'danger' });
    }
    return items;
  }, [allowedActions, handleView, handleEdit, handleCopy, handleDeleteClick]);

  // --- Column visibility handlers ---
  const handleApplyColumns = useCallback((newVisibleColumns: string[]) => {
    setVisibleColumns(newVisibleColumns);
    localStorage.setItem('property-terms-master-columns', JSON.stringify(newVisibleColumns));
  }, []);

  const handleResetColumns = useCallback(() => {
    const defaultVisible = columnDefinitions.filter(col => col.defaultVisible).map(col => col.id);
    setVisibleColumns(defaultVisible);
    localStorage.removeItem('property-terms-master-columns');
  }, []);

  // --- View mode options ---
  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View' },
  ];

  // --- Action buttons ---
  const actionButtons: ActionButton[] = useMemo(() => {
    const buttons: ActionButton[] = [];
    if (allowedActions.includes('add')) {
      buttons.push({
        id: 'add',
        icon: <IconSystem.add size={18} />,
        label: 'Add Property Terms',
        onClick: handleAddNew,
        tooltip: 'Add New Property Terms',
        variant: 'icon',
      });
    }
    // Term types live here, with the terms that use them (settings cleanup C9).
    if (allowedActions.includes('edit')) {
      buttons.push({
        id: 'term-types',
        icon: <IconSystem.tag size={18} />,
        label: 'Term Types',
        onClick: () => setTermTypesOpen(true),
        tooltip: 'Term Types',
        variant: 'icon',
      });
    }
    buttons.push({
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      onClick: () => refetch(),
      tooltip: 'Refresh',
      variant: 'icon',
    });
    return buttons;
  }, [allowedActions, refetch, handleAddNew]);

  // --- Filter Apply ---
  const handleFilterApply = useCallback(() => {
    setQueryParams(prev => ({
      ...prev,
      propertyId: filterProperty || undefined,
      termType: filterTermType || undefined,
      includeInactive: filterStatus === 'true' ? false : true,
      page: 1,
    }));
  }, [filterProperty, filterTermType, filterStatus]);

  // --- Filter Clear ---
  const handleFilterClear = useCallback(() => {
    setFilterProperty('');
    setFilterTermType('');
    setFilterStatus('');
    setQueryParams(prev => ({
      ...prev,
      propertyId: undefined,
      termType: undefined,
      includeInactive: undefined,
      page: 1,
    }));
  }, []);

  // --- RENDER ---
  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Property Terms"
        description="Manage property terms and policies for booking confirmations"
        icon={moduleIcon && (IconSystem as any)[moduleIcon]
          ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
          : undefined
        }
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search property terms...',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: viewModeOptions,
          onChange: (mode) => setViewMode(mode as ViewMode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen,
          appliedCount: [filterProperty, filterTermType, filterStatus].filter(Boolean).length,
        }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        customToolbarItems={
          <ColumnSelector
            columns={columnDefinitions}
            visibleColumns={visibleColumns}
            onApply={handleApplyColumns}
            onReset={handleResetColumns}
          />
        }
        actions={actionButtons}
      />

      {/* REQUIRED: marginTop: '6px' — the ONLY allowed inline style on .module-content */}
      <div className="module-content" style={{ marginTop: '6px' }}>
        <div className="data-grid-container">
          <SyutGrid
            data={data}
            columns={tableColumns}
            enableRowSelection
            enablePagination
            manualPagination
            totalCount={pagination.totalItems}
            currentPage={(queryParams.page ?? 1) - 1}
            pageSize={queryParams.pageSize ?? 25}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            rowActions={getRowActions}
            emptyMessage="No property terms found"
            frozenLeftColumns={['select', 'propertyName']}
            frozenRightColumns={['actions']}
            visibleColumns={visibleColumns}
          />
        </div>

        {/* REQUIRED: Filter panel as SIBLING of data-grid-container */}
        {filterOpen && <div className="search-panel">
          <div className="search-panel-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <IconSystem.filter size={14} />
              <h3 className="search-panel-title">Filters</h3>
            </div>
            <button
              onClick={() => setFilterOpen(false)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0.25rem',
                display: 'flex',
                alignItems: 'center',
                color: 'var(--text-muted)',
                borderRadius: '0.25rem',
              }}
              title="Close filters"
            >
              <IconSystem.x size={16} />
            </button>
          </div>

          <div className="search-panel-content">
            <div className="filter-section">
              <SyutSelect
                label="Property"
                isAsync
                loadOptions={loadPropertyFilterOptions}
                defaultOptions
                cacheOptions={false}
                value={filterProperty}
                onChange={(val: string | string[] | null) => setFilterProperty((val as string) || '')}
                placeholder="Select property..."
                isClearable
                size="sm"
              />
            </div>

            <div className="filter-section">
              <SyutSelect
                label="Term Type"
                options={termTypeOptions}
                value={filterTermType}
                onChange={(val: string | string[] | null) => setFilterTermType((val as string) || '')}
                placeholder="Select type..."
                isClearable
                size="sm"
              />
            </div>

            <div className="filter-section">
              <SyutSelect
                label="Status"
                options={statusOptions}
                value={filterStatus}
                onChange={(val: string | string[] | null) => setFilterStatus((val as string) || '')}
                placeholder="Select status..."
                isClearable={false}
                size="sm"
              />
            </div>
          </div>

          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={handleFilterClear}>
              Clear
            </button>
            <button className="btn btn-primary" onClick={handleFilterApply}>
              Apply
            </button>
          </div>
        </div>}
      </div>

      {/* Form Modal */}
      <PropertyTermsModal
        isOpen={formModalOpen}
        mode={formMode}
        record={selectedRecord}
        onSave={handleSaveSuccess}
        onClose={() => setFormModalOpen(false)}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreModal={handleRestoreModal}
        createInstanceId={createInstanceId}
        copyInstanceId={copyInstanceId}
        minimizeRef={formModalMinimizeRef}
        termTypeOptions={termTypeOptions}
      />

      <TermTypesModal isOpen={termTypesOpen} onClose={() => setTermTypesOpen(false)} onChanged={() => void loadLookups()} />

      {/* View Modal */}
      <PropertyTermsViewModal
        isOpen={viewModalOpen}
        record={selectedRecord}
        onClose={() => setViewModalOpen(false)}
        onEdit={(rec) => handleEdit(rec)}
        termTypeOptions={termTypeOptions}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreView={handleRestoreView}
      />

      {/* Delete Confirm */}
      <ConfirmDialog
        open={deleteModalOpen}
        title="Confirm Delete"
        message={`Are you sure you want to delete this property terms entry? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Deleting..."
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteModalOpen(false)}
      />
    </div>
  );
};

export default PropertyTermsMasterScreen;
